/** @jsxImportSource @emotion/react */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Column } from "primereact/column";
import { accessAPI, TablaFiltrable, Titulo, tituloBase } from "../../shared";

import { estadoCertificado, fechaCorta } from "../../portal/utilidades";
import { Card } from "primereact/card";
import { Tag } from "primereact/tag";
import SkeletonListado from "../SkeletonListado";

const CAMPOS_BUSQUEDA = [
  "numero",
  "proceso.nombrefantasia",
  "proceso.certificadotipo.nombre",
  "proceso.norma.numero",
  "_vencimiento"
];

export default function Certificados() {
  // Título de la pestaña, como en el admin
  useEffect(() => {
    document.title = "Certificados";
    return () => (document.title = tituloBase());
  }, []);

  const navigate = useNavigate();
  const [certificados, setCertificados] = useState(null);

  useEffect(() => {
    accessAPI(
      "GET",
      "cliente/instanciacertificado?todos=true",
      null,
      (r) =>
        setCertificados(
          r.map((c) => ({
            ...c,
            _estado: estadoCertificado(c),
            // Para el desplegable de estado de la tabla
            _estadoTexto: estadoCertificado(c).texto,
            _vencimiento: fechaCorta(c.fechavencimiento),
            // "Extendido": el titular lo extendió a otra empresa, o se lo
            // extendieron a este cliente.
            _extendido: !!c.extendido || c.extensioncertificado?.length > 0
          }))
        ),
      () => setCertificados([])
    );
  }, []);

  const estado = (c) => (
    <span css={{ display: "inline-flex", gap: "0.35rem", flexWrap: "wrap" }}>
      <Tag severity={c._estado.severidad}>{c._estado.texto}</Tag>
      {c.proceso?.certificadotipo?.sinrenovacion && (
        <Tag severity="danger" icon="pi pi-ban">No se renueva</Tag>
      )}
    </span>
  );

  const numero = (c) => (
    <span css={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" }}>
      <span css={{ fontWeight: 700, color: "var(--p-texto)" }}>{c.numero}</span>
      {c._extendido && <Tag severity="info">Extendido</Tag>}
    </span>
  );

  // Hasta tener los datos, el esqueleto de la sección (como en el admin)
  if (!certificados) return <SkeletonListado filtros={1} />;

  return (
    <>
      <Titulo
        // Flecha al Inicio, o a donde indique ?volver=
        urlVolver="/"
        subtitulo="Tus certificados de conformidad, su vigencia y documentación."
      >
        Certificados
      </Titulo>
      <Card css={{ marginBottom: "1em" }}>
        <TablaFiltrable
          value={certificados || []}
          searchFields={CAMPOS_BUSQUEDA}
          filtros={[
            { campo: "_estadoTexto", label: "Estado", labelPlural: "estados", siempreVisible: true }
          ]}
          nombreArchivo="certificados"
          emptyMessage="No hay certificados"
          className="p-tabla filas-clickeables"
          onRowClick={(e) => navigate(`/certificado/${e.data.id}`)}
        >
          <Column header="Certificado" body={numero} sortable sortField="numero" exportValue={(c) => c.numero} />
          <Column
            header="Nombre"
            body={(c) => (
              <>
                <div className="p-celda-titulo">{c.proceso?.nombrefantasia}</div>
                {c.proceso?.norma?.numero && (
                  <div className="p-celda-sub">{c.proceso.norma.numero}</div>
                )}
              </>
            )}
            exportValue={(c) => c.proceso?.nombrefantasia}
          />
          <Column header="Tipo de certificado" field="proceso.certificadotipo.nombre" />
          <Column
            header="Vencimiento"
            sortable
            sortField="fechavencimiento"
            body={(c) => (
              <div css={{ display: "flex", flexDirection: "column", gap: "0.25rem", alignItems: "flex-start" }}>
                <span>{c._vencimiento}</span>
                {c._estado.clave !== "noVigente" && c._estado.dias != null && (
                  <Tag
                    // Días hasta el vencimiento: rojo si venció, ámbar hasta 60, verde después
                    severity={c._estado.dias < 0 ? "danger" : c._estado.dias <= 60 ? "warning" : "success"}
                    icon={c._estado.dias < 0 ? "pi pi-calendar-times" : "pi pi-calendar"}
                  >
                    {c._estado.dias < 0 ? "Vencido" : `${c._estado.dias} ${c._estado.dias === 1 ? "día" : "días"}`}
                  </Tag>
                )}
              </div>
            )}
            exportValue={(c) => c._vencimiento}
          />
          <Column header="Estado" body={estado} exportValue={(c) => c._estadoTexto} />
        </TablaFiltrable>
      </Card>
    </>
  );
}
